import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Bot, Plus, Settings, Play, Pause, Trash2, Edit, Check, X,
  Inbox, List, ArrowUpDown, Calendar, MessageSquare, Sparkles,
  Clock, Zap, Save, TestTube, Loader2,
  ArrowRight, Workflow, LayoutGrid, Activity, Mail,
  Search, Copy, BookOpen, Database, Webhook
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CustomSelect } from '@/components/ui/custom-select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import AgentSkillsPanel from '@/pages/AgentSkills';

const TAB_KEYS = ['agents', 'workflow', 'skills', 'memory', 'schedules'];
const TAB_TRIGGER_CLASS =
  "data-[state=active]:bg-[#98139C] data-[state=active]:text-white data-[state=active]:shadow-sm " +
  "text-[#30302F] dark:text-white hover:text-[#98139C] dark:hover:text-[#d87bdb] " +
  "rounded-none border-b-2 border-transparent data-[state=active]:border-[#98139C] " +
  "px-4 h-10 font-medium transition-colors flex items-center gap-2";

// Friendly display labels for known categories. Unknown categories fall back to title-case.
const CATEGORY_LABELS = {
  intake: 'Intake & Discovery',
  backlog: 'Backlog Management',
  prioritization: 'Prioritization',
  planning: 'Sprint Planning',
  communication: 'Communication',
  ops: 'Ops & Integrations',
};

const titleCase = (s) => (s || '').replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const labelForCategory = (cat) => CATEGORY_LABELS[cat] || titleCase(cat) || 'Other';

// Single source of truth for model display names. Edit modal still saves the raw deployment id.
const MODEL_LABEL = {
  'gpt-5-chat': 'GPT-5',
  'gpt-5-mini': 'GPT-5 Mini',
  'gpt-5-pro': 'GPT-5 Pro',
  'gpt-5.3-chat': 'GPT-5.3',
  'gpt-5.2-chat': 'GPT-5.2',
  'gpt-5.1-chat': 'GPT-5.1',
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-haiku-4-5': 'Claude Haiku 4.5',
  'claude-opus-4-6': 'Claude Opus 4.6',
};
const modelLabel = (id) => MODEL_LABEL[id] || id;

// Pre-populated example inputs for the Test modal, keyed by agent id.
const TEST_EXAMPLES = {
  'agent-rice': JSON.stringify({
    title: 'Add dark mode',
    users_affected: 'all PMs',
    success_criteria: 'toggle persists',
  }, null, 2),
  'agent-duplicate': JSON.stringify({
    item: 'Add dark mode',
    candidates: ['Add light mode toggle', 'Theme switcher'],
  }, null, 2),
  'agent-readiness': JSON.stringify({
    title: 'As a PM, I want X so that Y',
    acceptance_criteria: ['Y is testable'],
    story_points: 3,
  }, null, 2),
  'agent-personas': 'A feature that lets product managers track sprint velocity in real time.',
  'agent-ado-scout': 'Scan the backlog for stale and blocked items and recommend next actions.',
};
const exampleFor = (id) =>
  TEST_EXAMPLES[id] || 'Add a feature that lets users export reports to PDF.';

// Strip ``` and ```json fences from model output before parsing/display.
const stripCodeFences = (raw) => {
  if (typeof raw !== 'string') return raw;
  let s = raw.trim();
  // Opening fence
  s = s.replace(/^```(?:json|JSON)?\s*\n?/, '');
  // Closing fence
  s = s.replace(/\n?```\s*$/, '');
  return s.trim();
};

// Lightweight cron-to-human converter. Falls back to "Custom schedule".
const cronToHuman = (expr) => {
  if (typeof expr !== 'string') return 'Custom schedule';
  const trimmed = expr.trim();
  if (!trimmed) return '';

  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return 'Custom schedule';

  const [min, hour, dom, month, dow] = parts;

  if (trimmed === '* * * * *') return 'every minute';

  const formatTime = (h, m) => {
    const hourNum = parseInt(h, 10);
    const minNum = parseInt(m, 10);
    if (Number.isNaN(hourNum) || Number.isNaN(minNum)) return null;
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum % 12 === 0 ? 12 : hourNum % 12;
    return `${displayHour}:${String(minNum).padStart(2, '0')} ${period}`;
  };

  const time = formatTime(hour, min);
  if (!time) return 'Custom schedule';

  // Daily at specific time (month and day wildcards)
  if (month === '*' && dom === '*') {
    if (dow === '1-5') return `${time}, weekdays`;
    if (dow === '*') return `${time}, every day`;
    if (dow === '1') return `${time}, Mondays`;
    if (dow === '2') return `${time}, Tuesdays`;
    if (dow === '3') return `${time}, Wednesdays`;
    if (dow === '4') return `${time}, Thursdays`;
    if (dow === '5') return `${time}, Fridays`;
    if (dow === '6') return `${time}, Saturdays`;
    if (dow === '0' || dow === '7') return `${time}, Sundays`;
  }

  // Monthly on specific day-of-month
  if (month === '*' && dow === '*' && /^\d+$/.test(dom)) {
    const day = parseInt(dom, 10);
    const suffix = (day === 1 || day === 21 || day === 31) ? 'st'
                 : (day === 2 || day === 22) ? 'nd'
                 : (day === 3 || day === 23) ? 'rd' : 'th';
    return `${time} on the ${day}${suffix} of every month`;
  }

  return 'Custom schedule';
};

// Phase colors used by the data-driven Workflow tab (cycles through by category index).
const PHASE_COLORS = [
  { ring: 'border-blue-400 group-hover:border-blue-500', bg: 'bg-blue-500', tint: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400', label: 'text-blue-500/70' },
  { ring: 'border-green-400 group-hover:border-green-500', bg: 'bg-green-500', tint: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400', label: 'text-green-500/70' },
  { ring: 'border-amber-400 group-hover:border-amber-500', bg: 'bg-amber-500', tint: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400', label: 'text-amber-500/70' },
  { ring: 'border-purple-400 group-hover:border-purple-500', bg: 'bg-purple-500', tint: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400', label: 'text-purple-500/70' },
  { ring: 'border-cyan-400 group-hover:border-cyan-500', bg: 'bg-cyan-500', tint: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400', label: 'text-cyan-500/70' },
  { ring: 'border-pink-400 group-hover:border-pink-500', bg: 'bg-pink-500', tint: 'bg-pink-50 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400', label: 'text-pink-500/70' },
];

const categoryIcons = {
  intake: Inbox,
  backlog: List,
  prioritization: ArrowUpDown,
  planning: Calendar,
  communication: MessageSquare,
  ops: Settings,
  general: Bot
};

const categoryColors = {
  intake: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  backlog: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  prioritization: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  planning: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400',
  communication: 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400',
  ops: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400',
  general: 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400'
};

const categoryBgColors = {
  intake: 'bg-blue-500',
  backlog: 'bg-green-500',
  prioritization: 'bg-amber-500',
  planning: 'bg-purple-500',
  communication: 'bg-pink-500',
  ops: 'bg-cyan-500',
  general: 'bg-gray-500'
};

const statusColors = {
  active: 'bg-green-500',
  inactive: 'bg-gray-400',
  planned: 'bg-amber-500'
};

const triggerLabels = {
  manual: { label: 'Manual', icon: Zap },
  scheduled: { label: 'Scheduled', icon: Clock },
  event: { label: 'Event-based', icon: Sparkles }
};

// Agent workflow architecture - defines connections between agents
const agentArchitecture = {
  stages: [
    {
      id: 'intake',
      name: 'Intake & Discovery',
      description: 'Raw ideas enter the system',
      color: 'blue',
      agents: ['agent-intake', 'agent-duplicate', 'agent-personas']
    },
    {
      id: 'backlog',
      name: 'Backlog Management',
      description: 'Refine and structure requirements',
      color: 'green',
      agents: ['agent-refinement']
    },
    {
      id: 'prioritization',
      name: 'Prioritization',
      description: 'Score and rank items',
      color: 'amber',
      agents: ['agent-rice']
    },
    {
      id: 'planning',
      name: 'Sprint Planning',
      description: 'Plan and schedule work',
      color: 'purple',
      agents: ['agent-sprint-planner']
    },
    {
      id: 'communication',
      name: 'Communication',
      description: 'Report and share progress',
      color: 'pink',
      agents: ['agent-standup', 'agent-stakeholder']
    }
  ],
  connections: [
    { from: 'agent-intake', to: 'agent-duplicate', label: 'Check duplicates' },
    { from: 'agent-intake', to: 'agent-personas', label: 'Generate personas' },
    { from: 'agent-duplicate', to: 'agent-refinement', label: 'Unique items' },
    { from: 'agent-personas', to: 'agent-refinement', label: 'With context' },
    { from: 'agent-refinement', to: 'agent-rice', label: 'Refined items' },
    { from: 'agent-rice', to: 'agent-sprint-planner', label: 'Prioritized backlog' },
    { from: 'agent-sprint-planner', to: 'agent-standup', label: 'Sprint data' },
    { from: 'agent-sprint-planner', to: 'agent-stakeholder', label: 'Progress data' }
  ]
};

export default function AgentSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [testInput, setTestInput] = useState('');
  const [testOutput, setTestOutput] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Tab state with URL ?tab= sync so deep links land on the right tab and
  // the route is consistent with the Reports page (also ?tab=). Default 'agents'.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab = TAB_KEYS.includes(rawTab) ? rawTab : 'agents';
  const setActiveTab = (next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'agents') params.delete('tab'); else params.set('tab', next);
      return params;
    }, { replace: true });
  };

  // Confirm-deactivate state for AlertDialog (B-5).
  const [deactivateConfirmAgent, setDeactivateConfirmAgent] = useState(null);

  // Copy affordance for Test modal output (B-7).
  const [testCopied, setTestCopied] = useState(false);
  
  // Pipeline execution state
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [executingAgent, setExecutingAgent] = useState(null);
  const [pipelineResults, setPipelineResults] = useState([]);
  const [showPipelineResults, setShowPipelineResults] = useState(false);
  
  // Products for pipeline selection
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  
  // Scheduled Jobs state
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    job_type: 'standup',
    cron_expression: '30 9 * * 1-5',
    name: '',
    config: { send_email: false, recipient_email: '', teams_webhook_url: '' }
  });
  const [sprints, setSprints] = useState([]);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingTeams, setTestingTeams] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);

  useEffect(() => {
    loadAgents();
    loadProducts();
    loadScheduledJobs();
    loadSprints();
    loadModels();
  }, []);

  // URL sync is now handled inside setActiveTab via useSearchParams.
  
  const loadProducts = async () => {
    try {
      const res = await api.get('/products/');
      setProducts(res.data);
      if (res.data.length > 0) {
        setSelectedProductId(res.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };
  
  const loadSprints = async () => {
    try {
      const res = await api.get('/sprints/');
      setSprints(res.data);
    } catch (err) {
      console.error('Failed to load sprints:', err);
    }
  };
  
  const loadModels = async () => {
    try {
      const res = await api.get('/agents/models/available');
      setAvailableModels(res.data.models || []);
    } catch (err) {
      console.error('Failed to load models:', err);
      // Fallback list, used only when /agents/models/available fails entirely.
      // Mirrors the actual Azure AI Services deployments. Marks gpt-4o as
      // not-deployed so picking it does not silently 404 at runtime.
      setAvailableModels([
        { id: 'gpt-5-chat', label: 'GPT-5', provider: 'azure-openai', is_deployed: true },
        { id: 'gpt-5-mini', label: 'GPT-5 Mini', provider: 'azure-openai', is_deployed: true },
        { id: 'gpt-5-pro', label: 'GPT-5 Pro', provider: 'azure-openai', is_deployed: true },
        { id: 'gpt-4.1', label: 'GPT-4.1', provider: 'azure-openai', is_deployed: true },
        { id: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', provider: 'azure-openai', is_deployed: true },
        { id: 'gpt-4o-mini', label: 'GPT-4o Mini', provider: 'azure-openai', is_deployed: true },
        { id: 'gpt-4o', label: 'GPT-4o', provider: 'azure-openai', is_deployed: false },
      ]);
    }
  };

  const loadScheduledJobs = async () => {
    try {
      const res = await api.get('/agents/scheduled-jobs');
      setScheduledJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Failed to load scheduled jobs:', err);
    }
  };
  
  const createScheduledJob = async () => {
    try {
      await api.post('/agents/scheduled-jobs', scheduleForm);
      setShowScheduleModal(false);
      setScheduleForm({
        job_type: 'standup',
        cron_expression: '30 9 * * 1-5',
        name: '',
        config: { send_email: false, recipient_email: '' }
      });
      loadScheduledJobs();
    } catch (err) {
      console.error('Failed to create job:', err);
      alert('Failed to create scheduled job: ' + (err.response?.data?.detail || err.message));
    }
  };
  
  const toggleScheduledJob = async (job) => {
    try {
      await api.patch(`/agents/scheduled-jobs/${job.id}`, { enabled: !job.enabled });
      loadScheduledJobs();
    } catch (err) {
      console.error('Failed to toggle job:', err);
    }
  };
  
  const deleteScheduledJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled job?')) return;
    try {
      await api.delete(`/agents/scheduled-jobs/${jobId}`);
      loadScheduledJobs();
    } catch (err) {
      console.error('Failed to delete job:', err);
    }
  };
  
  const runScheduledJobNow = async (jobId) => {
    try {
      await api.post(`/agents/scheduled-jobs/${jobId}/run`);
      alert('Job executed successfully!');
      loadScheduledJobs();
    } catch (err) {
      console.error('Failed to run job:', err);
      alert('Failed to run job: ' + (err.response?.data?.detail || err.message));
    }
  };
  
  const testEmailConfiguration = async () => {
    if (!scheduleForm.config.recipient_email) {
      alert('Please enter a recipient email address first');
      return;
    }
    setTestingEmail(true);
    try {
      await api.post('/agents/scheduled-jobs/test-email', {
        recipient_email: scheduleForm.config.recipient_email,
        job_type: scheduleForm.job_type
      });
      alert('Test email sent successfully! Check your inbox.');
    } catch (err) {
      console.error('Failed to send test email:', err);
      alert('Failed to send test email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTestingEmail(false);
    }
  };
  
  const testTeamsWebhook = async () => {
    if (!scheduleForm.config.teams_webhook_url) {
      alert('Please enter a Teams webhook URL first');
      return;
    }
    setTestingTeams(true);
    try {
      await api.post('/agents/scheduled-jobs/test-teams', {
        webhook_url: scheduleForm.config.teams_webhook_url,
        job_type: scheduleForm.job_type
      });
      alert('Test message sent to Teams successfully! Check your channel.');
    } catch (err) {
      console.error('Failed to send test to Teams:', err);
      alert('Failed to send to Teams: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTestingTeams(false);
    }
  };
  
  // Run a single agent (for test mode)
  const runSingleAgent = async (agent) => {
    setExecutingAgent(agent.id);
    try {
      const res = await api.post(`/agents/${agent.id}/test`, {
        test_input: `Test run for ${agent.name}`
      });
      setPipelineResults(prev => [...prev, {
        agent_id: agent.id,
        agent_name: agent.name,
        success: res.data.success !== false,
        output: res.data.output?.substring(0, 100) || 'Completed',
        latency_ms: res.data.latency_ms
      }]);
    } catch (err) {
      setPipelineResults(prev => [...prev, {
        agent_id: agent.id,
        agent_name: agent.name,
        success: false,
        output: err.message,
        latency_ms: 0
      }]);
    }
    setExecutingAgent(null);
  };
  
  // Run full pipeline with context sharing via backend
  const runPipeline = async () => {
    if (!selectedProductId) {
      alert('Please select a product to run the pipeline on');
      return;
    }
    
    setPipelineRunning(true);
    setPipelineResults([]);
    setShowPipelineModal(false);
    
    try {
      // Call the backend pipeline endpoint with context sharing
      const res = await api.post('/agents/pipeline/run', {
        product_id: selectedProductId,
        include_context: true
      });
      
      if (res.data.success) {
        // Transform backend response to frontend results format
        const results = [];
        for (const itemResult of res.data.results || []) {
          for (const stage of itemResult.stages || []) {
            results.push({
              agent_id: stage.agent_id,
              agent_name: stage.stage,
              item_title: itemResult.item_title,
              success: stage.success,
              output: typeof stage.output === 'object' 
                ? JSON.stringify(stage.output).substring(0, 150) + '...'
                : stage.output?.substring(0, 150) || 'Completed',
              error: stage.error
            });
          }
        }
        setPipelineResults(results);
        setShowPipelineResults(true);
      } else {
        alert('Pipeline failed: ' + (res.data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Pipeline error:', err);
      alert('Pipeline failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPipelineRunning(false);
      await loadAgents(); // Refresh to get updated run counts
    }
  };

  const loadAgents = async () => {
    try {
      const res = await api.get('/agents/');
      setAgents(res.data);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setEditForm({
      name: agent.name,
      description: agent.description,
      status: agent.status,
      category: agent.category,
      trigger: agent.trigger,
      model: agent.model,
      tasks: agent.tasks.join('\n'),
      system_prompt: agent.system_prompt
    });
    setShowEditModal(true);
  };

  const saveAgent = async () => {
    if (!selectedAgent) return;
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        tasks: editForm.tasks.split('\n').filter(t => t.trim())
      };
      await api.put(`/agents/${selectedAgent.id}`, payload);
      await loadAgents();
      setShowEditModal(false);
    } catch (err) {
      console.error('Failed to save agent:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleAgent = async (agentId) => {
    try {
      await api.post(`/agents/${agentId}/toggle`);
      await loadAgents();
    } catch (err) {
      console.error('Failed to toggle agent:', err);
    }
  };

  // B-5: confirm before deactivating an active agent; re-activate is immediate.
  const requestToggleAgent = (agent) => {
    if (agent.status === 'active') {
      setDeactivateConfirmAgent(agent);
    } else {
      toggleAgent(agent.id);
    }
  };

  const confirmDeactivate = async () => {
    if (!deactivateConfirmAgent) return;
    const id = deactivateConfirmAgent.id;
    setDeactivateConfirmAgent(null);
    await toggleAgent(id);
  };

  const openTestModal = (agent) => {
    setSelectedAgent(agent);
    // B-10: pre-populate with a useful example so the modal is immediately runnable.
    setTestInput(exampleFor(agent.id));
    setTestOutput(null);
    setTestCopied(false);
    setShowTestModal(true);
  };

  const runTest = async () => {
    if (!selectedAgent || !testInput.trim()) return;
    setTesting(true);
    setTestOutput(null);
    try {
      const res = await api.post(`/agents/${selectedAgent.id}/test`, {
        test_input: testInput
      });
      setTestOutput(res.data);
    } catch (err) {
      setTestOutput({ error: err.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const togglePromptExpand = (agentId) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [agentId]: !prev[agentId]
    }));
  };

  // Filter agents by search query (name or description, case-insensitive).
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredAgents = agents.filter(agent => {
    if (!normalizedSearch) return true;
    const name = (agent.name || '').toLowerCase();
    const desc = (agent.description || '').toLowerCase();
    return name.includes(normalizedSearch) || desc.includes(normalizedSearch);
  });

  // Group by category
  const agentsByCategory = filteredAgents.reduce((acc, agent) => {
    const cat = agent.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(agent);
    return acc;
  }, {});

  // B-1: data-driven categories — derive from the actual agents so nothing is hidden
  // (e.g., ADO Scout's `ops` category is no longer invisible).
  const categories = Array.from(new Set(agents.map(a => a.category || 'general'))).sort();

  // Workflow tab also needs all agents grouped by category, regardless of search filter.
  const allAgentsByCategory = agents.reduce((acc, agent) => {
    const cat = agent.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(agent);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8" data-testid="agent-settings-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 lg:mb-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Configuration</p>
          <h1 className="text-2xl lg:text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>
            AI Agents
          </h1>
          <p className="text-[#30302F] dark:text-white mt-1 lg:mt-2 font-light text-sm lg:text-lg">
            Configure and customize your PM workflow automation agents
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="flex items-center gap-2 text-[#30302F] dark:text-white text-xs mb-1">
            <Bot size={14} /> Total Agents
          </div>
          <p className="text-2xl font-bold text-[#30302F] dark:text-white">{agents.length}</p>
        </div>
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-xs mb-1">
            <Play size={14} /> Active
          </div>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {agents.filter(a => a.status === 'active').length}
          </p>
        </div>
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs mb-1">
            <Clock size={14} /> Planned
          </div>
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {agents.filter(a => a.status === 'planned').length}
          </p>
        </div>
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="flex items-center gap-2 text-[#98139C] text-xs mb-1">
            <Sparkles size={14} /> Total Runs
          </div>
          <p className="text-2xl font-bold text-[#98139C]">
            {agents.reduce((sum, a) => sum + (a.run_count || 0), 0)}
          </p>
        </div>
      </div>

      {/* Top-level Tabs: Agents | Workflow | Skills | Memory | Schedules */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full justify-start gap-1 bg-transparent border-b border-[#e3e3e3] dark:border-white/10 rounded-none h-auto p-0 mb-6">
          <TabsTrigger value="agents" className={TAB_TRIGGER_CLASS} data-testid="tab-agents">
            <LayoutGrid size={14} /> Agents
          </TabsTrigger>
          <TabsTrigger value="workflow" className={TAB_TRIGGER_CLASS} data-testid="tab-workflow">
            <Workflow size={14} /> Workflow
          </TabsTrigger>
          <TabsTrigger value="skills" className={TAB_TRIGGER_CLASS} data-testid="tab-skills">
            <BookOpen size={14} /> Skills
          </TabsTrigger>
          <TabsTrigger value="memory" className={TAB_TRIGGER_CLASS} data-testid="tab-memory">
            <Database size={14} /> Memory
          </TabsTrigger>
          <TabsTrigger value="schedules" className={TAB_TRIGGER_CLASS} data-testid="tab-schedules">
            <Clock size={14} /> Schedules
          </TabsTrigger>
        </TabsList>

        {/* WORKFLOW TAB — data-driven grid grouped by category */}
        <TabsContent value="workflow" className="mt-0">
        <div className="bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-lg overflow-hidden">
          {/* Canvas Header */}
          <div className="px-6 py-4 border-b border-[#e3e3e3] dark:border-white/10 bg-[#faf8fb] dark:bg-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#98139C] to-[#4F0077] flex items-center justify-center shadow-lg">
                <Workflow size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#30302F] dark:text-white">Agent Workflow</h2>
                <p className="text-xs text-[#30302F]/80 dark:text-white/80">Click nodes to configure • Run pipeline to execute</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Run Pipeline Button */}
              <button
                onClick={() => setShowPipelineModal(true)}
                disabled={pipelineRunning}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-60"
                data-testid="run-pipeline-btn"
              >
                {pipelineRunning ? (
                  <><Loader2 size={16} className="animate-spin" /> Running...</>
                ) : (
                  <><Zap size={16} /> Run Pipeline</>
                )}
              </button>
              
              {/* Legend */}
              <div className="flex items-center gap-3 text-xs border-l border-[#e3e3e3] dark:border-white/10 pl-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  <span className="text-[#30302F]/80 dark:text-white/80">Active</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="text-[#30302F]/80 dark:text-white/80">Planned</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Data-driven Workflow Canvas — grid grouped by category, one card per agent. */}
          <div
            className="relative overflow-x-auto p-6"
            style={{
              background: 'radial-gradient(circle at 1px 1px, rgba(152, 19, 156, 0.06) 1px, transparent 0)',
              backgroundSize: '20px 20px'
            }}
          >
            <div className="flex flex-col gap-6">
              {categories.map((category, catIdx) => {
                const phaseAgents = (allAgentsByCategory[category] || []);
                if (phaseAgents.length === 0) return null;
                const color = PHASE_COLORS[catIdx % PHASE_COLORS.length];
                const CategoryIcon = categoryIcons[category] || Bot;
                return (
                  <div key={`phase-${category}`} className="flex items-stretch gap-3">
                    {/* Phase label rail */}
                    <div className="flex flex-col items-center gap-2 w-28 flex-shrink-0">
                      <div className={`w-10 h-10 rounded-lg ${color.bg} flex items-center justify-center shadow`}>
                        <CategoryIcon size={18} className="text-white" />
                      </div>
                      <p className={`text-[10px] font-bold uppercase tracking-widest text-center ${color.label}`}>
                        {labelForCategory(category)}
                      </p>
                    </div>
                    {/* Arrow */}
                    <div className="flex items-center text-[#9ca3af] flex-shrink-0">
                      <ArrowRight size={20} />
                    </div>
                    {/* Agent nodes */}
                    <div className="flex flex-wrap gap-3 flex-1">
                      {phaseAgents.map((agent) => {
                        const isExecuting = executingAgent === agent.id;
                        const TriggerIcon = triggerLabels[agent.trigger]?.icon || Zap;
                        return (
                          <div
                            key={agent.id}
                            className="cursor-pointer group"
                            onClick={() => !pipelineRunning && isAdmin && openEditModal(agent)}
                            data-testid={`workflow-node-${agent.id}`}
                          >
                            <div className={`w-[180px] bg-white dark:bg-[#252542] rounded-xl border-2 shadow-md transition-all ${isExecuting ? 'border-green-500 ring-4 ring-green-400/30 scale-105' : `${color.ring} group-hover:shadow-xl`}`}>
                              <div className="p-3">
                                <div className={`w-8 h-8 rounded-lg ${color.bg} flex items-center justify-center mb-2`}>
                                  <Bot size={16} className="text-white" />
                                </div>
                                <p className="text-xs font-bold text-[#30302F] dark:text-white line-clamp-1">{agent.name}</p>
                                <p className="text-[10px] text-[#30302F]/70 dark:text-white/70 mb-2 line-clamp-2 min-h-[24px]">{agent.description}</p>
                                <div className="flex items-center gap-1.5">
                                  <Badge className={`text-[8px] ${color.tint} flex items-center gap-1`}>
                                    <TriggerIcon size={8} />
                                    {triggerLabels[agent.trigger]?.label || agent.trigger}
                                  </Badge>
                                  <div className={`w-2 h-2 rounded-full ${statusColors[agent.status]} ${isExecuting ? 'animate-ping' : ''}`} />
                                  {agent.run_count > 0 && (
                                    <span className="text-[8px] text-[#98139C] ml-auto">{agent.run_count}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Pipeline Results */}
          {pipelineResults.length > 0 && (
            <div className="px-6 py-4 border-t border-[#e3e3e3] dark:border-white/10 bg-[#faf8fb] dark:bg-white/5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#30302F] dark:text-white flex items-center gap-2">
                  <Activity size={14} className="text-[#98139C]" /> Pipeline Results
                </h3>
                <button 
                  onClick={() => setPipelineResults([])}
                  className="text-xs text-[#30302F]/70 dark:text-white/70 hover:text-[#98139C]"
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {pipelineResults.map((result, idx) => (
                  <div 
                    key={idx}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                      result.success 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                        : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    }`}
                  >
                    {result.success ? <Check size={12} /> : <X size={12} />}
                    {result.agent_name.replace(' Agent', '')}
                    {result.latency_ms > 0 && <span className="opacity-60">({result.latency_ms}ms)</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </TabsContent>

        {/* AGENTS TAB — cards grouped by category, with search */}
        <TabsContent value="agents" className="mt-0">
          <div className="flex items-center justify-end mb-4">
            <div className="relative w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#30302F]/60 dark:text-white/60 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search agents..."
                aria-label="Search agents"
                data-testid="agent-search"
                className="w-full h-9 pl-9 pr-3 text-sm rounded-full border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white placeholder:text-[#30302F]/50 dark:placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#98139C]/40 focus:border-[#98139C]"
              />
            </div>
          </div>

          {filteredAgents.length === 0 && (
            <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-12 text-center">
              <Bot size={48} className="mx-auto mb-4 text-[#30302F]/20 dark:text-white/20" />
              <h3 className="text-lg font-heading text-[#30302F] dark:text-white mb-2">No matching agents</h3>
              <p className="text-sm text-[#30302F]/80 dark:text-white/80">
                Try a different search term.
              </p>
            </div>
          )}

          {categories.map(category => {
        const categoryAgents = agentsByCategory[category];
        if (!categoryAgents || categoryAgents.length === 0) return null;

        const CategoryIcon = categoryIcons[category] || Bot;
        const categoryLabel = labelForCategory(category);

        return (
          <div key={category} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <CategoryIcon size={18} className="text-[#98139C]" aria-hidden="true" />
              <h2 className="text-lg font-bold text-[#30302F] dark:text-white capitalize">
                {categoryLabel}
              </h2>
              <Badge className={`text-[11px] ${categoryColors[category]}`}>
                {categoryAgents.length} agent{categoryAgents.length !== 1 ? 's' : ''}
              </Badge>
            </div>

            <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-hidden">
              <table className="w-full" aria-label={`${categoryLabel} agents`}>
                <thead className="bg-[#f9f5fa] dark:bg-white/5 border-b border-[#e3e3e3] dark:border-white/10">
                  <tr>
                    <th scope="col" className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Agent</th>
                    <th scope="col" className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Status</th>
                    <th scope="col" className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Trigger</th>
                    <th scope="col" className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Model</th>
                    <th scope="col" className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Runs</th>
                    <th scope="col" className="text-right px-4 py-3 text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryAgents.map(agent => {
                    const TriggerIcon = triggerLabels[agent.trigger]?.icon || Zap;

                    return (
                      <tr
                        key={agent.id}
                        className="border-b border-[#e3e3e3] dark:border-white/5 last:border-0 hover:bg-[#f9f9f9] dark:hover:bg-white/5"
                        data-testid={`agent-row-${agent.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#f0dcf0] dark:bg-[#98139C]/20 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                              <Bot size={16} className="text-[#98139C]" />
                            </div>
                            <div>
                              <p className="font-bold text-sm text-[#30302F] dark:text-white">{agent.name}</p>
                              <p className="text-xs text-[#30302F]/80 dark:text-white/80 line-clamp-1">{agent.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`text-[11px] ${
                            agent.status === 'active' ? 'bg-green-500 text-white' :
                            agent.status === 'planned' ? 'bg-amber-500 text-white' : 'bg-gray-400 text-white'
                          }`}>
                            {agent.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-[#30302F] dark:text-white">
                            <TriggerIcon size={14} aria-hidden="true" />
                            <span>{triggerLabels[agent.trigger]?.label || agent.trigger}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#30302F]/80 dark:text-white/80">
                          {modelLabel(agent.model)}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#30302F]/80 dark:text-white/80">
                          {agent.run_count > 0 ? (
                            <span className="text-[#98139C] font-medium">{agent.run_count}</span>
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isAdmin && (
                              <button
                                onClick={() => requestToggleAgent(agent)}
                                className={`p-2 rounded-full transition-colors ${
                                  agent.status === 'active'
                                    ? 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-white/5 dark:text-white'
                                }`}
                                title={agent.status === 'active' ? 'Deactivate agent' : 'Activate agent'}
                                aria-label={agent.status === 'active' ? `Deactivate ${agent.name}` : `Activate ${agent.name}`}
                                data-testid={`toggle-${agent.id}`}
                              >
                                {agent.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => openEditModal(agent)}
                                className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/5 dark:text-white transition-colors"
                                title="Edit agent"
                                aria-label={`Edit ${agent.name}`}
                                data-testid={`edit-${agent.id}`}
                              >
                                <Edit size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => openTestModal(agent)}
                              className="p-2 rounded-full bg-[#f0dcf0] text-[#98139C] hover:bg-[#e0cce0] dark:bg-[#98139C]/20 transition-colors"
                              title="Test agent"
                              aria-label={`Test ${agent.name}`}
                              data-testid={`test-${agent.id}`}
                            >
                              <TestTube size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
        </TabsContent>

        {/* SKILLS TAB — full markdown-first editor (import/export + preview). */}
        <TabsContent value="skills" className="mt-0">
          <AgentSkillsPanel />
        </TabsContent>

        {/* MEMORY TAB */}
        <TabsContent value="memory" className="mt-0">
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-12 text-center">
            <Database size={48} className="mx-auto mb-4 text-[#98139C]/60" />
            <h2 className="text-xl font-heading text-[#30302F] dark:text-white mb-2">Agent Memory</h2>
            <p className="text-sm text-[#30302F]/80 dark:text-white/80 max-w-md mx-auto">
              Long-term context shared across agent runs. Memory store and retrieval policies will appear here.
            </p>
          </div>
        </TabsContent>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <Settings size={20} className="text-[#98139C]" />
              Edit Agent: {selectedAgent?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Name
                </label>
                <Input
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Status
                </label>
                <CustomSelect
                  value={editForm.status || 'planned'}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full"
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                    { value: 'planned', label: 'Planned' },
                  ]}
                />
              </div>
            </div>
            
            <div>
              <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                Description
              </label>
              <Input
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Category
                </label>
                <CustomSelect
                  value={editForm.category || 'general'}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full"
                  options={[
                    { value: 'intake', label: 'Intake & Discovery' },
                    { value: 'backlog', label: 'Backlog Management' },
                    { value: 'prioritization', label: 'Prioritization' },
                    { value: 'planning', label: 'Sprint Planning' },
                    { value: 'communication', label: 'Communication' },
                  ]}
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Trigger
                </label>
                <CustomSelect
                  value={editForm.trigger || 'manual'}
                  onChange={(e) => setEditForm({ ...editForm, trigger: e.target.value })}
                  className="w-full"
                  options={[
                    { value: 'manual', label: 'Manual' },
                    { value: 'scheduled', label: 'Scheduled' },
                    { value: 'event', label: 'Event-based' },
                  ]}
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Model
                </label>
                <CustomSelect
                  value={editForm.model || 'gpt-5-chat'}
                  onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                  className="w-full"
                  options={(() => {
                    // Group by provider, deployed first within each group, and
                    // honor `is_deployed` so picking an un-deployed model is
                    // visually flagged instead of silently 404-ing at runtime.
                    const grouped = {};
                    availableModels.forEach((m) => {
                      const p = m.provider || 'Other';
                      if (!grouped[p]) grouped[p] = [];
                      grouped[p].push(m);
                    });
                    // Provider display order: deployed Azure OpenAI first.
                    const providerOrder = ['azure-openai', 'moonshot', 'anthropic'];
                    const providerLabels = {
                      'azure-openai': 'Azure AI Foundry (OpenAI)',
                      moonshot: 'Moonshot (third-party)',
                      anthropic: 'Anthropic (not deployed)',
                    };
                    const sortedProviders = Object.keys(grouped).sort((a, b) => {
                      const ai = providerOrder.indexOf(a);
                      const bi = providerOrder.indexOf(b);
                      if (ai === -1 && bi === -1) return a.localeCompare(b);
                      if (ai === -1) return 1;
                      if (bi === -1) return -1;
                      return ai - bi;
                    });
                    const opts = [];
                    sortedProviders.forEach((provider) => {
                      const models = grouped[provider]
                        .slice()
                        .sort((a, b) => {
                          if (a.is_deployed !== b.is_deployed) return a.is_deployed ? -1 : 1;
                          return (a.label || a.id).localeCompare(b.label || b.id);
                        });
                      const headerLabel = providerLabels[provider] || provider;
                      opts.push({ value: `__header_${provider}`, label: `--- ${headerLabel} ---`, disabled: true });
                      models.forEach((m) => {
                        const baseLabel = m.label || m.name || m.id;
                        const display = m.is_deployed ? baseLabel : `${baseLabel} (not deployed)`;
                        opts.push({ value: m.id, label: display, disabled: !m.is_deployed });
                      });
                    });
                    return opts.length > 0 ? opts : [
                      { value: 'gpt-5-chat', label: 'GPT-5' },
                      { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
                      { value: 'gpt-5-pro', label: 'GPT-5 Pro' },
                      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
                      { value: 'gpt-4.1', label: 'GPT-4.1' },
                    ];
                  })()}
                />
              </div>
            </div>
            
            <div>
              <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                Tasks (one per line)
              </label>
              <Textarea
                value={editForm.tasks || ''}
                onChange={(e) => setEditForm({ ...editForm, tasks: e.target.value })}
                rows={4}
                className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C] font-mono text-sm"
                placeholder="Parse unstructured feature requests&#10;Generate problem statements&#10;..."
              />
            </div>
            
            <div>
              <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                System Prompt
              </label>
              <Textarea
                value={editForm.system_prompt || ''}
                onChange={(e) => setEditForm({ ...editForm, system_prompt: e.target.value })}
                rows={12}
                className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C] font-mono text-sm"
                placeholder="You are a Product Management assistant..."
              />
            </div>
            
            <div className="flex gap-3 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
              <button
                onClick={saveAgent}
                disabled={saving}
                className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-11 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {saving ? (
                  <><Loader2 size={16} className="animate-spin" /> Saving...</>
                ) : (
                  <><Save size={16} /> Save Changes</>
                )}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-11 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <TestTube size={20} className="text-[#98139C]" />
              Test Agent: {selectedAgent?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                Test Input
              </label>
              <Textarea
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                rows={4}
                className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
                placeholder="Enter test input for the agent..."
              />
            </div>
            
            <button
              onClick={runTest}
              disabled={testing || !testInput.trim()}
              className="w-full bg-[#4F0077] hover:bg-[#3a0058] text-white rounded-full font-bold h-11 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              {testing ? (
                <><Loader2 size={16} className="animate-spin" /> Running Test...</>
              ) : (
                <><Play size={16} /> Run Test</>
              )}
            </button>
            
            {testOutput && (
              <div className="mt-4">
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Output
                </label>
                {testOutput.error ? (
                  <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 p-4">
                    <p className="text-sm text-red-600 dark:text-red-400">{testOutput.error}</p>
                  </div>
                ) : (() => {
                  // B-7: strip markdown code fences, try to JSON.parse for pretty-print, fall back to text.
                  const raw = testOutput.output ?? '';
                  const stripped = stripCodeFences(raw);
                  let displayed = stripped;
                  try {
                    const parsed = JSON.parse(stripped);
                    displayed = JSON.stringify(parsed, null, 2);
                  } catch {
                    displayed = stripped;
                  }
                  const handleCopy = async () => {
                    try {
                      await navigator.clipboard.writeText(raw);
                      setTestCopied(true);
                      setTimeout(() => setTestCopied(false), 1500);
                    } catch (err) {
                      console.error('Clipboard write failed:', err);
                    }
                  };
                  return (
                    <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4 relative">
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="absolute top-2 right-2 h-7 px-2 text-xs flex items-center gap-1 rounded-full border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/10 text-[#30302F] dark:text-white hover:bg-[#f9f5fa] dark:hover:bg-white/20 transition-colors"
                        aria-label="Copy output to clipboard"
                        data-testid="test-output-copy"
                      >
                        {testCopied ? (
                          <><Check size={12} className="text-green-600 dark:text-green-400" /> Copied</>
                        ) : (
                          <><Copy size={12} /> Copy</>
                        )}
                      </button>
                      <pre className="text-sm text-[#30302F] dark:text-white whitespace-pre-wrap font-mono pr-16">
                        {displayed}
                      </pre>
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#e3e3e3] dark:border-white/10 text-xs text-[#30302F] dark:text-white">
                        <span>~{testOutput.tokens_used} tokens</span>
                        <span>{testOutput.latency_ms}ms latency</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pipeline Selection Modal */}
      <Dialog open={showPipelineModal} onOpenChange={setShowPipelineModal}>
        <DialogContent className="max-w-md rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <Workflow size={20} className="text-[#98139C]" />
              Run Agent Pipeline
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#30302F]/80 dark:text-white/80">
              Select a product to run the AI agent pipeline. Each item in the product will be processed through:
            </p>
            
            <div className="bg-[#faf8fb] dark:bg-white/5 p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-[#30302F] dark:text-white">Duplicate Check</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-[#30302F] dark:text-white">Refinement (with context)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-[#30302F] dark:text-white">RICE Scoring</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="text-[#30302F] dark:text-white">Sprint Planning</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-500" />
                <span className="text-[#30302F] dark:text-white">Quality Scout</span>
              </div>
            </div>
            
            <div>
              <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                Select Product
              </label>
              <CustomSelect
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                data-testid="pipeline-product-select"
                className="w-full"
                placeholder="-- Select a product --"
                options={[
                  { value: '', label: '-- Select a product --' },
                  ...products.map(p => ({ value: p.id, label: p.name }))
                ]}
              />
            </div>
            
            <p className="text-xs text-[#30302F]/70 dark:text-white/70">
              <strong>Context Sharing:</strong> Each agent will receive outputs from previous agents, creating a connected intelligence pipeline.
            </p>
            
            <div className="flex gap-3 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
              <button
                onClick={runPipeline}
                disabled={!selectedProductId || pipelineRunning}
                className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-11 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                data-testid="confirm-run-pipeline-btn"
              >
                {pipelineRunning ? (
                  <><Loader2 size={16} className="animate-spin" /> Running...</>
                ) : (
                  <><Zap size={16} /> Run Pipeline</>
                )}
              </button>
              <button
                onClick={() => setShowPipelineModal(false)}
                className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-11 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Job Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-lg rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <Clock size={20} className="text-[#98139C]" />
              Schedule Automated Job
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-[#30302F] dark:text-white">Job Type</label>
              <CustomSelect
                value={scheduleForm.job_type}
                onChange={e => setScheduleForm({...scheduleForm, job_type: e.target.value})}
                className="w-full mt-1"
                options={[
                  { value: 'standup', label: 'Daily Standup' },
                  { value: 'report', label: 'Report Generation' },
                ]}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#30302F] dark:text-white">Job Name</label>
              <Input
                value={scheduleForm.name}
                onChange={e => setScheduleForm({...scheduleForm, name: e.target.value})}
                placeholder="e.g., Morning Standup"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#30302F] dark:text-white">Schedule (Cron Expression)</label>
              <Input
                value={scheduleForm.cron_expression}
                onChange={e => setScheduleForm({...scheduleForm, cron_expression: e.target.value})}
                placeholder="30 9 * * 1-5"
                className="mt-1 font-mono"
                data-testid="cron-input"
              />
              {/* B-13: live human-readable cron preview */}
              <p
                className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium"
                data-testid="cron-preview"
              >
                {cronToHuman(scheduleForm.cron_expression) || ' '}
              </p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70 mt-1">
                Format: minute hour day month weekday
              </p>
            </div>
            
            {scheduleForm.job_type === 'standup' && (
              <div>
                <label className="text-sm font-medium text-[#30302F] dark:text-white">Sprint (optional)</label>
                <CustomSelect
                  value={scheduleForm.config.sprint_id || ''}
                  onChange={e => setScheduleForm({...scheduleForm, config: {...scheduleForm.config, sprint_id: e.target.value || null}})}
                  className="w-full mt-1"
                  options={[
                    { value: '', label: 'Current Active Sprint' },
                    ...sprints.map(s => ({ value: s.id, label: s.name }))
                  ]}
                />
              </div>
            )}
            
            {scheduleForm.job_type === 'report' && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#30302F] dark:text-white">Report Type</label>
                  <CustomSelect
                    value={scheduleForm.config.report_type || 'weekly'}
                    onChange={e => setScheduleForm({...scheduleForm, config: {...scheduleForm.config, report_type: e.target.value}})}
                    className="w-full mt-1"
                    options={[
                      { value: 'weekly', label: 'Weekly Progress' },
                      { value: 'stakeholder_update', label: 'Stakeholder Update' },
                      { value: 'team_sprint_report', label: 'Team Sprint Report' },
                    ]}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#30302F] dark:text-white">Audience</label>
                  <CustomSelect
                    value={scheduleForm.config.audience || 'stakeholder'}
                    onChange={e => setScheduleForm({...scheduleForm, config: {...scheduleForm.config, audience: e.target.value}})}
                    className="w-full mt-1"
                    options={[
                      { value: 'executive', label: 'Executive' },
                      { value: 'stakeholder', label: 'Stakeholder' },
                      { value: 'technical', label: 'Technical' },
                    ]}
                  />
                </div>
              </>
            )}
            
            <div className="flex items-center gap-3 p-3 bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
              <input
                type="checkbox"
                id="send_email"
                checked={scheduleForm.config.send_email || false}
                onChange={e => setScheduleForm({...scheduleForm, config: {...scheduleForm.config, send_email: e.target.checked}})}
                className="w-4 h-4"
              />
              <label htmlFor="send_email" className="text-sm text-[#30302F] dark:text-white">Send email notification</label>
            </div>
            
            {scheduleForm.config.send_email && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-[#30302F] dark:text-white">Recipient Email</label>
                  <Input
                    type="email"
                    value={scheduleForm.config.recipient_email || ''}
                    onChange={e => setScheduleForm({...scheduleForm, config: {...scheduleForm.config, recipient_email: e.target.value}})}
                    placeholder="email@example.com"
                    className="mt-1"
                  />
                </div>
                <button
                  type="button"
                  onClick={testEmailConfiguration}
                  disabled={testingEmail || !scheduleForm.config.recipient_email}
                  className="h-9 px-4 text-sm border border-[#98139C] text-[#98139C] rounded-full hover:bg-[#f0dcf0] dark:hover:bg-[#98139C]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="test-email-btn"
                >
                  {testingEmail ? (
                    <><Loader2 size={14} className="animate-spin" /> Sending...</>
                  ) : (
                    <><Mail size={14} /> Send Test Email</>
                  )}
                </button>
              </div>
            )}
            
            {/* Teams Webhook Section */}
            <div className="border-t border-[#e3e3e3] dark:border-white/10 pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                {/* B-12: replaced failing external SVG with a lucide Webhook icon. */}
                <Webhook size={18} className="text-[#5558AF]" aria-hidden="true" />
                <span className="text-sm font-medium text-[#30302F] dark:text-white">Microsoft Teams Integration</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-[#30302F] dark:text-white">Webhook URL</label>
                  <Input
                    type="url"
                    value={scheduleForm.config.teams_webhook_url || ''}
                    onChange={e => setScheduleForm({...scheduleForm, config: {...scheduleForm.config, teams_webhook_url: e.target.value}})}
                    placeholder="https://outlook.office.com/webhook/..."
                    className="mt-1"
                  />
                  <p className="text-xs text-[#30302F]/70 dark:text-white/70 mt-1">
                    <a 
                      href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#98139C] hover:underline"
                    >
                      How to create a Teams webhook →
                    </a>
                  </p>
                </div>
                {scheduleForm.config.teams_webhook_url && (
                  <button
                    type="button"
                    onClick={testTeamsWebhook}
                    disabled={testingTeams}
                    className="h-9 px-4 text-sm border border-[#5558AF] text-[#5558AF] rounded-full hover:bg-[#5558AF]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="test-teams-btn"
                  >
                    {testingTeams ? (
                      <><Loader2 size={14} className="animate-spin" /> Sending...</>
                    ) : (
                      <><MessageSquare size={14} /> Test Teams Webhook</>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
              <button
                onClick={createScheduledJob}
                className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-11 flex items-center justify-center gap-2 transition-colors"
              >
                <Clock size={16} /> Create Schedule
              </button>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-11 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SCHEDULES TAB */}
      <TabsContent value="schedules" className="mt-0">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-[#30302F] dark:text-white flex items-center gap-2">
                <Clock size={18} className="text-[#98139C]" />
                Scheduled Jobs
              </h2>
              <p className="text-sm text-[#30302F]/80 dark:text-white/80 mt-1">
                Automate your PM workflows with scheduled agent runs
              </p>
            </div>
            <button
              onClick={() => setShowScheduleModal(true)}
              className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-10 px-5 flex items-center gap-2 transition-colors"
              data-testid="create-schedule-btn"
            >
              <Plus size={16} /> New Schedule
            </button>
          </div>
          
          {scheduledJobs.length === 0 ? (
            <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-12 text-center">
              <Clock size={48} className="mx-auto mb-4 text-[#30302F]/20 dark:text-white/20" />
              <h3 className="text-lg font-heading text-[#30302F] dark:text-white mb-2">No Scheduled Jobs</h3>
              <p className="text-sm text-[#30302F]/80 dark:text-white/80 mb-4">
                Create automated schedules for standups, reports, and other agent tasks
              </p>
              <button
                onClick={() => setShowScheduleModal(true)}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-10 px-5 inline-flex items-center gap-2 transition-colors"
              >
                <Plus size={16} /> Create Your First Schedule
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {scheduledJobs.map(job => (
                <div 
                  key={job.id}
                  className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4"
                  data-testid={`scheduled-job-${job.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-[#30302F] dark:text-white">
                          {job.name || `${job.job_type.charAt(0).toUpperCase() + job.job_type.slice(1)} Job`}
                        </h3>
                        <Badge className={`text-[10px] ${job.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                          {job.enabled ? 'Active' : 'Paused'}
                        </Badge>
                        <Badge className="text-[10px] bg-[#f0dcf0] text-[#4F0077] dark:bg-[#98139C]/20 dark:text-[#d87bdb]">
                          {job.job_type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-[#30302F]/80 dark:text-white/80">
                        <span className="font-mono bg-[#faf8fb] dark:bg-white/5 px-2 py-0.5">{job.cron_expression}</span>
                        {job.next_run_time && (
                          <span>Next run: {new Date(job.next_run_time).toLocaleString()}</span>
                        )}
                        {job.run_count > 0 && (
                          <span>Runs: {job.run_count}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => runScheduledJobNow(job.id)}
                        className="h-8 px-3 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white text-xs hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors flex items-center gap-1"
                        title="Run Now"
                      >
                        <Play size={12} /> Run Now
                      </button>
                      <button
                        onClick={() => toggleScheduledJob(job)}
                        className={`h-8 px-3 border text-xs transition-colors flex items-center gap-1 ${
                          job.enabled 
                            ? 'border-amber-300 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/10' 
                            : 'border-green-300 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/10'
                        }`}
                        title={job.enabled ? 'Pause' : 'Enable'}
                      >
                        {job.enabled ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Enable</>}
                      </button>
                      <button
                        onClick={() => deleteScheduledJob(job.id)}
                        className="h-8 px-3 border border-red-200 text-red-600 text-xs hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors flex items-center gap-1"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Cron Help */}
          <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
            <h4 className="text-sm font-medium text-[#30302F] dark:text-white mb-3">Common Cron Schedules</h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
              <div className="bg-white dark:bg-white/5 p-2 border border-[#e3e3e3] dark:border-white/10">
                <code className="text-[#98139C]">30 9 * * 1-5</code>
                <p className="text-[#30302F]/80 dark:text-white/80 mt-1">9:30 AM weekdays</p>
              </div>
              <div className="bg-white dark:bg-white/5 p-2 border border-[#e3e3e3] dark:border-white/10">
                <code className="text-[#98139C]">0 8 * * 1</code>
                <p className="text-[#30302F]/80 dark:text-white/80 mt-1">8:00 AM Mondays</p>
              </div>
              <div className="bg-white dark:bg-white/5 p-2 border border-[#e3e3e3] dark:border-white/10">
                <code className="text-[#98139C]">0 17 * * 5</code>
                <p className="text-[#30302F]/80 dark:text-white/80 mt-1">5:00 PM Fridays</p>
              </div>
              <div className="bg-white dark:bg-white/5 p-2 border border-[#e3e3e3] dark:border-white/10">
                <code className="text-[#98139C]">0 9 1 * *</code>
                <p className="text-[#30302F]/80 dark:text-white/80 mt-1">9:00 AM 1st of month</p>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>
      </Tabs>

      {/* B-5: confirm before deactivating an active agent */}
      <AlertDialog
        open={!!deactivateConfirmAgent}
        onOpenChange={(open) => { if (!open) setDeactivateConfirmAgent(null); }}
      >
        <AlertDialogContent className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#30302F] dark:text-white">
              Deactivate {deactivateConfirmAgent?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#30302F]/80 dark:text-white/80">
              The agent will stop accepting runs. You can re-activate it any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="deactivate-cancel"
              className="rounded-full border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white hover:bg-[#f9f5fa] dark:hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeactivate}
              data-testid="deactivate-confirm"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
